/**
 * Query-string helpers for /search — must stay in sync with Search.js updateSearchParams.
 */

export const SEARCH_URL_RESERVED_KEYS = new Set([
  "q",
  "categories",
  "priceMin",
  "priceMax",
  "sort",
  "page",
]);

export function deleteAttributeFilterParamsFromSearchParams(searchParams) {
  for (const key of [...searchParams.keys()]) {
    if (SEARCH_URL_RESERVED_KEYS.has(key)) continue;
    if (/^\d+$/.test(key)) {
      searchParams.delete(key);
    }
  }
}

export function parseOptionalPositiveFloat(raw) {
  if (raw == null || raw === "") return null;
  const n = parseFloat(String(raw).trim());
  return !Number.isNaN(n) && n > 0 ? n : null;
}

/**
 * True when URL carries any search context worth hydrating (broader than legacy hasQueryParams).
 */
export function hasMeaningfulSearchParams(searchParams) {
  const q = searchParams.get("q");
  if (q != null && String(q).trim() !== "") return true;

  const categories = searchParams.get("categories");
  if (categories != null && String(categories).trim() !== "") return true;

  if (
    parseOptionalPositiveFloat(searchParams.get("priceMin")) != null ||
    parseOptionalPositiveFloat(searchParams.get("priceMax")) != null
  ) {
    return true;
  }

  const sort = searchParams.get("sort");
  if (sort != null && String(sort).trim() !== "") return true;

  const page = parseInt(searchParams.get("page"), 10);
  if (!Number.isNaN(page) && page > 1) return true;

  for (const [key] of searchParams.entries()) {
    if (SEARCH_URL_RESERVED_KEYS.has(key)) continue;
    if (!/^\d+$/.test(key)) continue;
    const val = searchParams.get(key);
    if (val != null && String(val).trim() !== "") return true;
  }

  return false;
}

/**
 * Inverse of bracket serialization in updateSearchParams for user-defined attribute filters.
 */
function parseBracketAttributeValue(raw) {
  const s = String(raw).trim();
  if (!s.startsWith("[") || !s.endsWith("]")) return null;
  const inner = s.slice(1, -1);
  const parts = inner.split(",").map((p) => p.trim());
  if (parts.length === 3) {
    const valueFrom = parseMaybeNum(parts[0]);
    const valueTo = parseMaybeNum(parts[1]);
    const type = parseInt(parts[2], 10);
    return {
      itemAttributeValues: null,
      userAttributeValues: {
        valueFrom: valueFrom ?? 0,
        valueTo: valueTo ?? 0,
        value: null,
        type: Number.isNaN(type) ? null : type,
      },
    };
  }
  if (parts.length === 2) {
    const type = parseInt(parts[1], 10);
    const rawVal = parts[0];
    const num = parseFloat(rawVal);
    const value = Number.isNaN(num) ? rawVal : num;
    return {
      itemAttributeValues: null,
      userAttributeValues: {
        valueFrom: null,
        valueTo: null,
        value,
        type: Number.isNaN(type) ? null : type,
      },
    };
  }
  return null;
}

function parseMaybeNum(s) {
  if (s === "" || s == null) return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

/**
 * Builds catAttrs-shaped entries from unknown numeric keys in the query string.
 */
export function parseAttributeFiltersFromSearchParams(searchParams) {
  const out = [];
  for (const [key, rawValue] of searchParams.entries()) {
    if (SEARCH_URL_RESERVED_KEYS.has(key)) continue;
    if (!/^\d+$/.test(key)) continue;
    const val = rawValue != null ? String(rawValue).trim() : "";
    if (!val) continue;

    const categoryAttributeId = Number.parseInt(key, 10);
    if (Number.isNaN(categoryAttributeId)) continue;

    if (val.startsWith("[")) {
      const parsed = parseBracketAttributeValue(val);
      if (parsed) {
        out.push({
          categoryAttributeId,
          ...parsed,
        });
      }
      continue;
    }

    const ids = val
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => Number.parseFloat(x))
      .filter((n) => !Number.isNaN(n));

    if (ids.length > 0) {
      out.push({
        categoryAttributeId,
        itemAttributeValues: ids,
        userAttributeValues: null,
      });
    }
  }
  return out;
}
